import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PlatformLogo from "@/components/PlatformLogo";
import { http, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput, PasswordRules, passwordValid } from "@/components/PasswordInput";
import LanguageSelector from "@/components/LanguageSelector";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const [np, setNp] = useState("");
  const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const ok = passwordValid(np) && np === cf;

  const submit = async (e) => {
    e.preventDefault();
    if (!ok) return;
    setBusy(true);
    try {
      await http.post("/auth/reset-password", { token, new_password: np });
      setDone(true);
    } catch (er) { toast.error(apiErr(er)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F172A] p-6" data-testid="reset-page">
      <div className="absolute top-4 right-4"><LanguageSelector /></div>
      <div className="w-full max-w-sm bg-white rounded-2xl p-6">
        <div className="mb-4"><PlatformLogo size={32} dark={false} testId="reset-platform-logo" /></div>
        <div className="flex items-center gap-2 mb-1"><KeyRound className="w-5 h-5 text-sky-600" /><h1 className="font-heading text-xl font-bold text-[#0F172A]">{t("auth.reset_title")}</h1></div>
        {!token ? (
          <p className="text-sm text-red-600 mt-4">{t("auth.reset_invalid")}</p>
        ) : done ? (
          <div className="mt-4 text-center space-y-4" data-testid="reset-done">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <p className="text-sm text-slate-600">{t("auth.reset_done")}</p>
            <Button onClick={() => nav("/login")} className="bg-[#0F172A]" data-testid="reset-go-login">{t("auth.sign_in")}</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <p className="text-sm text-slate-500">{t("auth.reset_sub")}</p>
            <div><Label>{t("auth.new_password")}</Label><div className="mt-1"><PasswordInput data-testid="reset-new" value={np} onChange={(e) => setNp(e.target.value)} /></div></div>
            <div><Label>{t("auth.confirm_new_password")}</Label><div className="mt-1"><PasswordInput data-testid="reset-confirm" value={cf} onChange={(e) => setCf(e.target.value)} /></div></div>
            <PasswordRules value={np} />
            {cf && np !== cf && <p className="text-xs text-red-500">{t("errors.password_mismatch")}</p>}
            <Button type="submit" data-testid="reset-submit" disabled={busy || !ok} className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B]">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {t("auth.reset_cta")}
            </Button>
            <Link to="/login" className="flex items-center justify-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" /> {t("auth.back_login")}</Link>
          </form>
        )}
      </div>
    </div>
  );
}
